// src/AdminOrderDetails.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useTheme } from "./contexts/ThemeContext";

import jsPDF from "jspdf";
import { Home, ArrowLeft, Printer, FileDown, Truck, Mail } from "lucide-react";
import logoSrc from "./assets/logo.png";

import couriers from "./data/couriers.json";
import shippingConfig from "./data/shippingConfig.json";
import emailjs from "emailjs-com";

export default function AdminOrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Shipment state
  const [courierId, setCourierId] = useState("");
  const [waybill, setWaybill] = useState("");
  const [savingShipment, setSavingShipment] = useState(false);
  const [isEditingShipment, setIsEditingShipment] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const ref = doc(db, "orders", orderId);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setLoading(false);
        return;
      }

      const data = snap.data();

      setOrder({
        id: snap.id,
        orderNumber: data.orderNumber || `BB-${snap.id.slice(0, 8)}`,
        createdAt: data.createdAt || null,
        customer: data.customer || { name: "-", email: "-", mobile: "-", address: "-" },
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total ?? 0,
        shippingCharge: data.shippingCharge ?? 0,
        grandTotal: data.grandTotal ?? data.total ?? 0,
        status: data.status || "Pending",
        shipment: data.shipment || null,
        ...data
      });

      if (data.shipment) {
        setCourierId(data.shipment.courierId || "");
        setWaybill(data.shipment.waybill || "");
        setIsEditingShipment(false);
      } else {
        setIsEditingShipment(true);
      }
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  async function updateStatus(newStatus) {
    if (!order) return;
    try {
      setUpdatingStatus(true);
      await updateDoc(doc(db, "orders", order.id), { status: newStatus });
      setOrder(prev => ({ ...prev, status: newStatus }));
    } finally {
      setUpdatingStatus(false);
    }
  }

  function printOrder() {
    window.print();
  }

  /* ---------- EMAIL ---------- */
 
  async function sendCustomerEmail() {
    if (!order) return;

    try {
      setSendingEmail(true);

      // PRODUCTS
      const orders = order.items.map(i => ({


        name: i.name,
        units: i.qty,
        price: i.price,

        image_url: i.img?.startsWith("http")
          ? i.img
          : `https://bubblebutter.in/${i.img}`

       // image_url: i.img || ""
      }));

      // ✅ SUBTOTAL (FIX)
      const subtotal = order.items.reduce(
        (sum, i) => sum + Number(i.price) * Number(i.qty || 1),
        0
      );
    


       // 🔍 DEBUG — ADD THIS LINE
      console.log("EMAIL IMAGE URL:", orders[0]?.image_url);

      // STATUS META
      const statusMeta = (() => {
        switch (order.status) {
          case "Shipped":
            return {
              status_message: "Your order has been shipped and is on the way 🚚",
              status_color: "#2563eb",
              show_shipping: true
            };
          case "Delivered":
            return {
              status_message: "Your order has been delivered successfully 🎉",
              status_color: "#458500",
              show_shipping: true
            };
          case "Cancelled":
            return {
              status_message: "Your order has been cancelled",
              status_color: "#dc2626",
              show_shipping: false
            };
          default:
            return {
              status_message: "Your order is being processed",
              status_color: "#f59e0b",
              show_shipping: false
             
            };
        }
      })();
      
      const subjectStatus =  order.status === "Pending" ? "Confirmed" : order.status;


      await emailjs.send(
        shippingConfig.emailJSServiceID2,
        shippingConfig.emailJSTemplateID2,
        {
          // REQUIRED FOR DELIVERY
          to_email: order.customer.email,

          // FOOTER DISPLAY
          email: order.customer.email,

          // BASIC
          order_id: order.orderNumber,
          website_url: "https://bubblebutter.in",

          // STATUS
          order_status: order.status,
          subject_status: subjectStatus,
          status_message: statusMeta.status_message,
          status_color: statusMeta.status_color,

          // SHIPPING
          show_shipping: statusMeta.show_shipping,
          courier: order.shipment?.courierName || "—",
          tracking_id: order.shipment?.waybill || "—",
          tracking_url: order.shipment?.trackingUrl || "",

          // PRODUCTS
          orders,

          // COST
          cost: {
            subtotal: subtotal,                 // ⭐ REQUIRED
            shipping: order.shippingCharge ?? 0,
            tax: order.tax ?? 0,
            total: order.grandTotal ?? order.total
          }
        },
        shippingConfig.emailJSPublicKey2
      );

      alert("Email sent successfully");
    } catch (err) {
      console.error("EmailJS Error:", err);
      alert("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  }




  /* ---------- PDF helper ---------- */

  async function loadOptimizedPNG(src, maxWidth = 300) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // PNG keeps transparency
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  /* ---------- PDF  ---------- */
  async function downloadPDF() {
    if (!order) return;

    const pdf = new jsPDF({
      unit: "pt",
      format: "a4",
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;
    let y = 40;

    /* ---------- LOGO (TRANSPARENT PNG) ---------- */
    try {
      const logoData = await loadOptimizedPNG(logoSrc, 300);
      pdf.addImage(logoData, "PNG", margin, y, 70, 40);
    } catch (e) {
      console.warn("Logo load failed", e);
    }
    /* ---------- HEADER ---------- */
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.text("INVOICE", pageWidth - margin, y + 14, { align: "right" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text("Bubble Butter", pageWidth - margin, y + 34, { align: "right" });
    pdf.text(
      "Handmade Beauty • Pure & Natural",
      pageWidth - margin,
      y + 48,
      { align: "right" }
    );

    y += 90;

    /* ---------- ORDER INFO ---------- */
    pdf.setFontSize(11);
    pdf.text(`Order #: ${order.orderNumber}`, margin, y);
    pdf.text(
      `Date: ${
        order.createdAt?.toDate
          ? order.createdAt.toDate().toLocaleString()
          : "-"
      }`,
      margin,
      y + 16
    );

    /* ---------- CUSTOMER ---------- */
    const custX = pageWidth / 2 + 10;
    pdf.setFont("helvetica", "bold");
    pdf.text("Bill To:", custX, y);

    pdf.setFont("helvetica", "normal");
    pdf.text(order.customer.name || "-", custX, y + 16);
    pdf.text(order.customer.email || "-", custX, y + 32);
    pdf.text(order.customer.mobile || "-", custX, y + 48);

    const addrLines = pdf.splitTextToSize(
      order.customer.address || "-",
      pageWidth - custX - margin
    );
    pdf.text(addrLines, custX, y + 64);

    y += Math.max(100, addrLines.length * 14 + 60);

    /* ---------- COLUMN SYSTEM ---------- */
    const rightEdge = pageWidth - margin;
    const totalAmountX = rightEdge;
    const totalCurrencyX = rightEdge - 42;
    const priceAmountX = rightEdge - 90;
    const priceCurrencyX = rightEdge - 132;
    const qtyColX = rightEdge - 180;
    const itemColX = margin;

    /* ---------- TABLE HEADER ---------- */
    pdf.setFont("helvetica", "bold");
    pdf.line(margin, y, rightEdge, y);
    y += 18;

    pdf.text("Item", itemColX, y);
    pdf.text("Qty", qtyColX, y, { align: "right" });
    pdf.text("Price", priceCurrencyX, y);
    pdf.text("Line Total", totalCurrencyX, y);

    y += 10;
    pdf.line(margin, y, rightEdge, y);
    y += 14;

    /* ---------- ITEMS ---------- */
    pdf.setFont("helvetica", "normal");

    let subtotal = 0;

    for (const it of order.items) {
      const qty = it.qty ?? 1;
      const price = Number(it.price ?? 0);
      const lineTotal = qty * price;
      subtotal += lineTotal;

      const nameLines = pdf.splitTextToSize(
        it.name || "-",
        qtyColX - itemColX - 10
      );

      if (y + nameLines.length * 14 > pageHeight - 160) {
        pdf.addPage();
        y = margin;
      }

      pdf.text(nameLines, itemColX, y);
      pdf.text(`${qty}`, qtyColX, y, { align: "right" });

      pdf.text("Rs.", priceCurrencyX, y);
      pdf.text(`${price}`, priceAmountX, y, { align: "right" });

      pdf.text("Rs.", totalCurrencyX, y);
      pdf.text(`${lineTotal}`, totalAmountX, y, { align: "right" });

      y += nameLines.length * 14 + 6;
    }

    /* ---------- SUMMARY ---------- */
    y += 20;
    pdf.line(priceCurrencyX - 10, y, rightEdge, y);
    y += 18;

    pdf.text("Subtotal:", priceCurrencyX - 10, y);
    pdf.text("Rs.", totalCurrencyX, y);
    pdf.text(`${subtotal}`, totalAmountX, y, { align: "right" });

    y += 16;
    pdf.text("Shipping:", priceCurrencyX - 10, y);
    pdf.text("Rs.", totalCurrencyX, y);
    pdf.text(`${order.shippingCharge ?? 0}`, totalAmountX, y, { align: "right" });

    y += 18;
    pdf.setFont("helvetica", "bold");
    pdf.text("Total:", priceCurrencyX - 10, y);
    pdf.text("Rs.", totalCurrencyX, y);
    pdf.text(
      `${order.grandTotal ?? subtotal}`,
      totalAmountX,
      y,
      { align: "right" }
    );

    /* ---------- FOOTER ---------- */
    const footerY = pageHeight - 36;
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      "Thank you for shopping with Bubble Butter!",
      pageWidth / 2,
      footerY - 6,
      { align: "center" }
    );
    pdf.text(
      "This is a computer generated invoice and does not require a signature.",
      pageWidth / 2,
      footerY + 6,
      { align: "center" }
    );

    pdf.save(`invoice-${order.orderNumber}.pdf`);
  }


  async function saveShipment() {
    if (!courierId || !waybill) {
      alert("Select courier and enter waybill number");
      return;
    }

    const courier = couriers.find(c => c.id === courierId);
    const trackingUrl = courier.trackingUrl.replace("{WAYBILL}", waybill);

    try {
      setSavingShipment(true);
      await updateDoc(doc(db, "orders", order.id), {
        shipment: {
          courierId,
          courierName: courier.name,
          waybill,
          trackingUrl,
          updatedAt: new Date()
        }
      });

      setOrder(prev => ({
        ...prev,
        shipment: {
          courierId,
          courierName: courier.name,
          waybill,
          trackingUrl
        }
      }));

      setIsEditingShipment(false);
    } finally {
      setSavingShipment(false);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading…</div>;
  }

  if (!order) {
    return <div className="p-6">Order not found</div>;
  }

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen p-6 bg-gradient-to-b from-pink-100 to-white dark:from-gray-900 dark:to-gray-800 text-gray-900 dark:text-gray-200">

        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm mb-4">
          <Home size={16} />
          <span onClick={() => navigate("/admin")} className="cursor-pointer hover:underline">
            Dashboard
          </span>
          <span>/</span>
          <span className="font-semibold text-pink-600">{order.orderNumber}</span>
        </div>

        {/* Header buttons */}
       <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg flex gap-2">
            <ArrowLeft size={18} /> Back
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={printOrder} className="px-4 py-2 bg-white dark:bg-gray-700 rounded-lg shadow flex gap-2">
              <Printer size={18} /> Print
            </button>

            <button onClick={downloadPDF} className="px-4 py-2 bg-pink-600 text-white rounded-lg shadow flex gap-2">
              <FileDown size={18} /> Invoice
            </button>

            <button
              onClick={sendCustomerEmail}
              disabled={sendingEmail}
              className="px-4 py-2 bg-green-600 text-white rounded-lg shadow flex gap-2"
            >
              <Mail size={18} />
              {sendingEmail ? "Sending..." : "Send Email"}
            </button>
          </div>
        </div>

        {/* Main card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow">

          <h2 className="font-semibold">Customer</h2>
          <div>{order.customer.name}</div>
          <div>{order.customer.email}</div>
          <div>{order.customer.mobile}</div>
          <div className="text-sm">{order.customer.address}</div>

          <h2 className="font-semibold mt-6 mb-2">Items</h2>
          {order.items.map((item, i) => (
            <div key={i} className="flex justify-between bg-pink-50 dark:bg-gray-700 p-3 rounded-lg mb-2">
              <div>{item.name} × {item.qty}</div>
              <div>₹{item.price}</div>
            </div>
          ))}

          <div className="mt-4 text-right">
            <div>Subtotal: ₹{order.total}</div>
            <div>Shipping: ₹{order.shippingCharge ?? 0}</div>
            <div className="text-xl font-bold text-pink-600">
              Grand Total: ₹{order.grandTotal}
            </div>
          </div>

          <div className="mt-6">
            <h2 className="font-semibold">Order Status</h2>
            <select
              value={order.status}
              onChange={e => updateStatus(e.target.value)}
              className="mt-2 p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
            >
              <option>Pending</option>
              <option>Shipped</option>
              <option>Delivered</option>
              <option>Cancelled</option>
            </select>
          </div>

          <div className="mt-8 border-t pt-6">
            <h2 className="font-semibold flex items-center gap-2 mb-3">
              <Truck size={18} /> Shipment Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select
                value={courierId}
                onChange={e => setCourierId(e.target.value)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                disabled={!isEditingShipment}
              >
                <option value="">Select Courier</option>
                {couriers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <input
                value={waybill}
                onChange={e => setWaybill(e.target.value)}
                placeholder="Waybill number"
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                disabled={!isEditingShipment}
              />

              <button
                onClick={isEditingShipment ? saveShipment : () => setIsEditingShipment(true)}
                disabled={savingShipment}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg"
              >
                {isEditingShipment ? (savingShipment ? "Saving..." : "Save Shipment") : "Edit Shipment"}
              </button>
            </div>

            {order.shipment?.trackingUrl && (
              <a
                href={order.shipment.trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="block mt-3 text-sm text-pink-600 underline"
              >
                Track Shipment
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
